import React, {PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState} from 'react';

import Col from '../../common/Col';
import {computePercentageChange} from '../../common/util';
import {GameLogicError} from '../errors';
import {useGameStatus} from '../hooks';
import {
  computeFuelPrice,
  computeSeasonCarbonEarningsPerTonne,
  computeSeasonTransportCostPerTonne,
  computeTonnesHarvested,
  formatSmallMoney,
  isHarvestYear,
} from '../mechanics';
import {getEvent} from '../resources/events';
import {getQuestion} from '../resources/questions';
import {HistoryYear} from '../types';
import ArrowButton from './buttons/ArrowButton';
import DotsLoader from './DotsLoader';
import InfoBox from './InfoBox';
import MessageBox from './MessageBox';
import {SimplePill} from './SimplePill';
import YieldCounter from './YieldCounter';

type Props = {
  onContinue: () => void;
};

type Entry = {
  message: JSX.Element;
  theYield: number;
};

const HarvestReport = ({onContinue}: Props): ReactElement => {
  const gameStatus = useGameStatus();
  const [messageIndex, setMessageIndex] = useState(0);
  const interval = useRef<ReturnType<typeof setInterval>>();

  // Get the history of years belonging to current harvest only
  const seasonHistory = useMemo(() => {
    const lastHarvestYear = gameStatus.harvestYears[gameStatus.harvestYears.length - 1];
    const seasonStart = lastHarvestYear !== undefined ? lastHarvestYear + 1 : 0;
    return gameStatus.history.slice(seasonStart);
  }, [gameStatus.harvestYears, gameStatus.history]);

  // Filter season history by keeping only questions and events that resulted in a yield change
  const seasonEffectiveHistory: HistoryYear[] = useMemo(
    () =>
      seasonHistory.map(year => ({
        choices: year.choices.filter(
          choice => choice.option?.yieldBefore && choice.option?.yieldBefore !== choice.option?.yieldAfter
        ),
        events: year.events.filter(event => event.effect && event.effect.yieldBefore !== event.effect.yieldAfter),
      })),
    [seasonHistory]
  );

  const currentYield = GameLogicError.assertNonNullish(gameStatus.currentYield);

  // Take the yieldBefore of the first event or question, whatever happened before. Fall back to currentYield
  const initialYield = useMemo(() => {
    for (const year of seasonEffectiveHistory) {
      if (year.choices[0]?.option) return year.choices[0].option.yieldBefore;
      if (year.events[0]) return year.events[0].effect?.yieldBefore ?? 0;
    }
    return currentYield;
  }, [currentYield, seasonEffectiveHistory]);

  const maxYield = initialYield * 1.5;

  const entries = useMemo<Entry[]>(() => {
    const crop = GameLogicError.assertNonNullish(gameStatus.crop);

    const results: Entry[] = [
      {
        message: (
          <span>
            Predicted yield of {crop.name} in your land is <Pos>{initialYield.toFixed(2)} tonnes/ha/year</Pos>
          </span>
        ),
        theYield: initialYield,
      },
    ];

    seasonEffectiveHistory.forEach(year => {
      year.choices.forEach(choice => {
        const option = getQuestion(choice.question).options.find(opt => opt.id === choice.option?.id);
        if (!choice.option || !option) return;
        const name = option.shortName || option.id;
        results.push({
          message: renderYieldChange(name, choice.option.yieldBefore, choice.option.yieldAfter),
          theYield: choice.option.yieldAfter,
        });
      });
      year.events.forEach(log => {
        const event = getEvent(log.id);
        if (!log.effect || !event) return;
        results.push({
          message: renderYieldChange(event.name, log.effect.yieldBefore, log.effect.yieldAfter),
          theYield: log.effect.yieldAfter,
        });
      });
    });

    results.push({
      message: (
        <span>
          Your land produced <Pos>{computeTonnesHarvested(gameStatus).toFixed(2)}t</Pos> this season!
        </span>
      ),
      theYield: results[results.length - 1]?.theYield ?? 0,
    });

    /* Add line about income */
    const pricePerTonne = computeFuelPrice(gameStatus);
    const carbonCreditPerTonne = computeSeasonCarbonEarningsPerTonne(gameStatus);
    const carbonCreditFlat = gameStatus.prices.carbonCreditFlat;

    const income = [
      <span key={0}>
        You will get <Pos>{formatSmallMoney(pricePerTonne)}/tonne</Pos> harvested
      </span>,
    ];
    if (gameStatus.prices.carbonCreditPerTonne)
      income.push(
        <span key={1}>
          , plus <Pos>{formatSmallMoney(carbonCreditPerTonne)}/tonne</Pos> as carbon credit
        </span>
      );
    if (gameStatus.prices.carbonCreditFlat)
      income.push(
        <span key={2}>
          , plus <Pos>{formatSmallMoney(carbonCreditFlat)}</Pos> as carbon credit
        </span>
      );

    results.push({
      message: <>{income}</>,
      theYield: results[results.length - 1]?.theYield ?? 0,
    });

    /* Add line about expenses */
    const {harvestCostPerTonne, dryCostPerTonne} = gameStatus.prices;
    const transportCostPerTonne = computeSeasonTransportCostPerTonne(gameStatus);

    if (harvestCostPerTonne || dryCostPerTonne || transportCostPerTonne) {
      const expenses = [<span key={0}>You need to spend </span>];
      if (harvestCostPerTonne)
        expenses.push(
          <span key={1}>
            <Neg>{formatSmallMoney(gameStatus.prices.harvestCostPerTonne)}/tonne</Neg> for harvesting
          </span>
        );
      if (dryCostPerTonne) {
        if (harvestCostPerTonne) {
          if (transportCostPerTonne) expenses.push(<span>, </span>);
          else expenses.push(<span> and </span>);
        }
        expenses.push(
          <span key={2}>
            <Neg>{formatSmallMoney(gameStatus.prices.dryCostPerTonne)}/tonne</Neg> for drying
          </span>
        );
      }
      if (transportCostPerTonne) {
        if (harvestCostPerTonne || dryCostPerTonne) expenses.push(<span> and </span>);
        expenses.push(
          <span key={3}>
            <Neg>{formatSmallMoney(transportCostPerTonne)}/tonne</Neg> for transport
          </span>
        );
      }
      results.push({
        message: <>{expenses}</>,
        theYield: results[results.length - 1]?.theYield ?? 0,
      });
    }

    return results;
  }, [gameStatus, initialYield, seasonEffectiveHistory]);

  useEffect(() => {
    interval.current = setInterval(() => {
      setMessageIndex(prevState => {
        if (prevState < entries.length - 1) {
          return prevState + 1;
        }
        if (interval.current) clearInterval(interval.current);
        return prevState;
      });
    }, 1200);
    return () => {
      if (interval.current) clearInterval(interval.current);
    };
  }, [entries.length]);

  if (!isHarvestYear(gameStatus)) {
    return <MessageBox>Something went wrong</MessageBox>;
  }

  const shownEntries = entries.slice(0, messageIndex + 1);
  const liveYield = shownEntries.length ? shownEntries[shownEntries.length - 1].theYield : 0;
  const fullyLoaded = messageIndex >= entries.length - 1;

  return (
    <MessageBox
      top={<SimplePill>Harvest</SimplePill>}
      bottom={
        <div className="flex justify-center">
          <ArrowButton direction="right" onClick={onContinue} disabled={!fullyLoaded}>
            Harvest
          </ArrowButton>
        </div>
      }
    >
      <Col className={'pt-md px-xl mobile:p-sm text-lg'}>
        <YieldCounter currentYield={liveYield} maxYield={maxYield} />
        <p className={'mb-xs text-3xl text-center font-semibold mobile:text-2xl'}>Harvest season report</p>
        <InfoBox className={'flex-col text-start text-xl font-semibold mobile:text-lg'}>
          <ul className={'w-full mt-xs pl-lg mobile:pl-sm list-disc list-outside marker:text-primary-300'}>
            {shownEntries.map((entry, idx) => (
              <li key={idx} className={'mb-2 first-letter:capitalize'}>
                {entry.message}
              </li>
            ))}
          </ul>
          {!fullyLoaded && <DotsLoader className={'mt-sm'} />}
        </InfoBox>
      </Col>
    </MessageBox>
  );
};

export default HarvestReport;

function renderYieldChange(cause: string, yieldBefore: number, yieldAfter: number) {
  const percent = computePercentageChange(yieldBefore, yieldAfter);
  let verb;
  let textClass;

  if (percent < 1) {
    verb = 'decreased';
    textClass = 'text-red-200';
  } else {
    verb = 'increased';
    textClass = 'text-primary-600';
  }

  return (
    <span>
      {cause} {verb} your yield by <span className={textClass}>{Math.abs(percent).toFixed(0)}%</span>
    </span>
  );
}

const Pos = ({children}: PropsWithChildren<{}>) => <span className={'text-primary-600'}>{children}</span>;
const Neg = ({children}: PropsWithChildren<{}>) => <span className={'text-red-200'}>{children}</span>;
